import { useEffect, useState, useMemo } from 'react';
import { VideoTrimmerClient } from "../Utils/videoUtils/videoTrim.client";
import { SkitterBookLivePagesDataProvider } from '../Data/DataProviders/SkitterBookLivePagesDataProvider';
import { VideoClipMaker } from '../Controls/VideoClipMaker';
import { DockPanelHandle } from '../Controls/DockPanel/DockPanel';
import { SkitterBookProperties } from './SkitterBookProperties';
import { SkitterBookTitlePage } from '../Models/SkitterBookTitlePage';
import { SkitterBookViewport } from '../Models/SkitterBookViewport';
import { SkitterbookLivePagesRequest } from '../Models/SkitterbookLivePagesRequest';
import { SkitterBookLivePrintButton } from './SkitterBookLivePrintButton';
import { arrayBufferToFile } from '../Utils/ConversionUtils';
import { uint8ArrayToBase64 } from '../Utils/ConversionUtils';
import { SkitterBookPrintMargin } from '../Models/SkitterBookPrintMargin';
import { SkitterBookPrintMarginProfile } from '../Models/SkitterBookPrintMarginProfile';
import { getSetting, setSetting } from '../Utils/settingsUtils';
import { equidistantFrames } from '../Utils/ImageUtils';
import './css/SkitterBookMaker.css';

const PRINT_MARGIN_PROFILE = "printMarginProfile";

interface SkitterBookMakerProps {
    videoBuffer: ArrayBuffer | null;
    videoName: string | null;
    dockPanel: React.RefObject<DockPanelHandle | null>;
}

export const SkitterBookMaker: React.FC<SkitterBookMakerProps> = ({ videoBuffer, videoName, dockPanel }) => {

    const [startSecs, setStartSecs] = useState<number | null>(null);
    const [durationSecs, setDurationSecs] = useState<number | null>(null);
    const [videoFrames, setVideoFrames] = useState<Uint8Array[] | null>(null);
    const [livePagesRequest, setLivePagesRequest] = useState<SkitterbookLivePagesRequest>();
    const [printMarginsDate, setPrintMarginsDate] = useState<number | null>(null);
    const [frameRate, setFrameRate] = useState(0);

    const extractor = useMemo(() => new VideoTrimmerClient(), []);

    useEffect(() => {
        if (videoFrames && frameRate) {
            const serialisableFrames = videoFrames.map(f => uint8ArrayToBase64(f));

            const titleImageBytes = serialisableFrames[Math.floor(serialisableFrames.length / 2)]; // take the middle frame of the clip as cover image

            const titleImageViewport: SkitterBookViewport = { height: 240, width: 520, x: 0, y: 0 }
            const titlePage: SkitterBookTitlePage = { id: crypto.randomUUID(), lastUpdated: new Date().toISOString(), title: "Working title", subTitle: "Working subtitle", titleImageBytes: titleImageBytes, isSample: true, titleImageViewport: titleImageViewport }
            const viewport: SkitterBookViewport = { height: 240, width: 320, x: 0, y: 0 }

            var frontPrintMargin: SkitterBookPrintMargin = { topMm: 0, leftMm: 0, bottomMm: 0, rightMm: 0 };
            var backPrintMargin: SkitterBookPrintMargin = { topMm: 0, leftMm: 0, bottomMm: 0, rightMm: 0 };

            const printMarginProfileString = getSetting(PRINT_MARGIN_PROFILE, null);

            if (printMarginProfileString) {
                const pmp: SkitterBookPrintMarginProfile = JSON.parse(printMarginProfileString);
                frontPrintMargin = pmp.frontPrintMargin;
                backPrintMargin = pmp.backPrintMargin;
                setPrintMarginsDate(pmp.lastChangedOn);
            }

            // get the front and back page margins from local storage here.

            const lpr: SkitterbookLivePagesRequest = {
                Frames: equidistantFrames(serialisableFrames, 80), // No matter how long the clip, 80 frames are always taken
                ImageMarginMm: 2,
                Rotation: '0',
                frameRate: frameRate,
                Viewport: viewport,
                TitlePage: titlePage,
                printerType: "SINGLE",
                paperSize: "A4",
                frontPrintMargin: frontPrintMargin,
                backPrintMargin: backPrintMargin
            };

            setLivePagesRequest(lpr);
        }
    }, [videoFrames, frameRate]);

    useEffect(() => {
        if (startSecs !== null && durationSecs && videoBuffer) {

            const videoClipFile = arrayBufferToFile(videoBuffer, "vidClip", "video/mp4")

            extractor.trimToBytes(videoClipFile, startSecs, durationSecs, (result) => {
                // This currently return bitmap images - to easy server bandwidth this should be changed to png. - however, to make it work - it's bmp.
                setVideoFrames(result);
            }, (err) => { console.log(`trimToBytes.error:${err}`) })
        }
    }, [startSecs, durationSecs, videoBuffer]);

    return (
        <div className='skitterbook-maker-container'>
            <SkitterBookLivePagesDataProvider request={livePagesRequest}>
                <div className='skitterbook-maker-inner-container'>
                    <div className="top-container">
                        <div className="video-clip-maker-container">
                            <VideoClipMaker videoBuffer={videoBuffer} videoName={videoName}
                                title="Create a clip from your selected video"
                                clipSelected={(result) => {
                                    setStartSecs(result.startSecs);
                                    setDurationSecs(result.durationSecs);
                                    setFrameRate(result.frameRate);
                                }} />
                        </div>
                    </div>
                    <div className="middle-container">
                        <SkitterBookProperties livePagesRequest={livePagesRequest}
                            title="Choose how you want your flipbook to appear"
                            dockPanel={dockPanel}
                            onChange={result => {
                                setLivePagesRequest(result);
                            }}
                        />
                    </div>
                    <div className="bottom-container">
                        <div className="skitterbook-live-print-container">
                            <SkitterBookLivePrintButton skitterBookLivePagesRequest={livePagesRequest}
                                profileLastChangedOn={printMarginsDate}
                                dockPanel={dockPanel}
                                onChange={result => {
                                    setLivePagesRequest(result);

                                    const profile: SkitterBookPrintMarginProfile = {
                                        profileName: "printMarginProfile",
                                        lastChangedOn: Date.now(),
                                        frontPrintMargin: result.frontPrintMargin,
                                        backPrintMargin: result.backPrintMargin
                                    };

                                    setSetting(profile.profileName, JSON.stringify(profile));
                                }}
                                onPageMarginClear={() => {
                                    setSetting(PRINT_MARGIN_PROFILE, null);
                                    setPrintMarginsDate(null);
                                    const zeroMargin: SkitterBookPrintMargin = {
                                        topMm: 0,
                                        leftMm: 0,
                                        bottomMm: 0,
                                        rightMm: 0
                                    };

                                    setLivePagesRequest(current => {

                                        if (!current) {
                                            return current;
                                        }

                                        return {
                                            ...current,
                                            frontPrintMargin: zeroMargin,
                                            backPrintMargin: zeroMargin
                                        };
                                    });
                                }} />
                        </div>
                    </div>
                </div>
            </SkitterBookLivePagesDataProvider>
        </div>
    );
};
